import React, { useState } from "react";
import {
  AvatarGroup,
  Box,
  Chip,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddReactionOutlined,
  DeleteOutline,
  EditOutlined,
  InsertDriveFileOutlined,
  ImageOutlined,
  MoreHoriz,
  PictureAsPdfOutlined,
  ReplyOutlined,
  SyncAlt,
} from "@mui/icons-material";
import { TEAMS_PURPLE } from "./TeamsConnection";
import User from "../../models/User";
import { CHAT_QUICK_REACTIONS, ChatAttachment, ChatMessage } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import Composer, { ComposerSubmit } from "./Composer";
import {
  formatChatTime,
  formatFileSize,
  formatListTime,
  segmentBody,
  userDisplayName,
} from "./chatUtils";

const MORE_REACTIONS = [
  ...CHAT_QUICK_REACTIONS,
  "👀",
  "🙏",
  "✅",
  "❌",
  "🔥",
  "💯",
  "👏",
  "🤔",
  "😅",
  "🚀",
  "📦",
  "⏰",
];

interface MessageItemProps {
  message: ChatMessage;
  meId: number;
  usersById: Map<number, User>;
  mentionCandidates: User[];
  /** Hide avatar and name when the previous message is from the same author a moment earlier. */
  compact: boolean;
  onOpenThread?: (messageId: number) => void;
  onToggleReaction: (message: ChatMessage, emoji: string) => void;
  onEdit: (message: ChatMessage, input: ComposerSubmit) => Promise<void>;
  onDelete: (message: ChatMessage) => void;
  onOpenAttachment: (attachment: ChatAttachment) => void;
}

function attachmentIcon(attachment: ChatAttachment) {
  if (attachment.contentType.startsWith("image/")) return <ImageOutlined fontSize="small" />;
  if (attachment.contentType === "application/pdf") return <PictureAsPdfOutlined fontSize="small" />;
  return <InsertDriveFileOutlined fontSize="small" />;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  meId,
  usersById,
  mentionCandidates,
  compact,
  onOpenThread,
  onToggleReaction,
  onEdit,
  onDelete,
  onOpenAttachment,
}) => {
  const [hover, setHover] = useState(false);
  const [editing, setEditing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reactionAnchor, setReactionAnchor] = useState<HTMLElement | null>(null);

  const author = message.authorId != null ? usersById.get(message.authorId) : undefined;
  const mine = message.authorId === meId;
  const deleted = Boolean(message.deletedAt);
  const mentionsMe = message.mentionUserIds.includes(meId);
  const showToolbar = hover && !deleted && !editing;

  const fromTeams = message.source === "teams";
  const teamsMarker =
    fromTeams || message.teamsSynced ? (
      <Tooltip title={fromTeams ? "Sent from Microsoft Teams" : "Also delivered in Microsoft Teams"}>
        <Typography
          component="span"
          variant="caption"
          sx={{ color: TEAMS_PURPLE, display: "inline-flex", alignItems: "center", gap: 0.25 }}
        >
          <SyncAlt sx={{ fontSize: 12 }} />
          {fromTeams ? "via Teams" : "Teams"}
        </Typography>
      </Tooltip>
    ) : null;

  const reactionNames = (userIds: number[]) =>
    userIds.map((id) => (id === meId ? "You" : userDisplayName(usersById.get(id)))).join(", ");

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: "relative",
        display: "flex",
        gap: 1.5,
        px: 2.5,
        pt: compact ? 0.25 : 1.25,
        pb: 0.25,
        bgcolor: hover ? "rgba(0,0,0,0.025)" : undefined,
      }}
    >
      <Box sx={{ width: 32, flexShrink: 0, pt: compact ? 0 : 0.25 }}>
        {!compact ? (
          <ChatAvatar user={author} size={32} />
        ) : (
          hover && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", lineHeight: "22px", display: "block", textAlign: "right" }}
            >
              {formatChatTime(message.createdAt)}
            </Typography>
          )
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!compact && (
          <Stack direction="row" alignItems="baseline" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {userDisplayName(author)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatListTime(message.createdAt) === formatChatTime(message.createdAt)
                ? formatChatTime(message.createdAt)
                : `${formatListTime(message.createdAt)} ${formatChatTime(message.createdAt)}`}
            </Typography>
            {message.editedAt && !deleted && (
              <Typography variant="caption" color="text.secondary">
                Edited
              </Typography>
            )}
            {!deleted && teamsMarker}
          </Stack>
        )}

        {editing ? (
          <Box sx={{ mt: 0.5, mb: 1 }}>
            <Composer
              placeholder="Edit message"
              mentionCandidates={mentionCandidates}
              usersById={usersById}
              initialBody={message.body}
              initialMentionIds={message.mentionUserIds}
              autoFocus
              dense
              onCancel={() => setEditing(false)}
              onSubmit={async (input) => {
                await onEdit(message, input);
                setEditing(false);
              }}
            />
          </Box>
        ) : deleted ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            This message has been deleted.
          </Typography>
        ) : (
          message.body && (
            <Box
              sx={{
                borderLeft: mentionsMe ? "3px solid" : undefined,
                borderColor: "error.light",
                pl: mentionsMe ? 1 : 0,
              }}
            >
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}
              >
                {segmentBody(message.body, message.mentionUserIds, usersById).map((segment, i) => {
                  if (segment.kind === "mention") {
                    return (
                      <Box
                        key={i}
                        component="span"
                        sx={{
                          color: segment.userId === meId ? "error.dark" : "primary.dark",
                          bgcolor: segment.userId === meId ? "rgba(196,49,75,0.1)" : "rgba(98,100,167,0.12)",
                          fontWeight: 600,
                          borderRadius: 0.5,
                          px: 0.25,
                        }}
                      >
                        {segment.text}
                      </Box>
                    );
                  }
                  if (segment.kind === "link") {
                    return (
                      <Link key={i} href={segment.text} target="_blank" rel="noopener noreferrer">
                        {segment.text}
                      </Link>
                    );
                  }
                  return <React.Fragment key={i}>{segment.text}</React.Fragment>;
                })}
                {compact && message.editedAt && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                    (edited)
                  </Typography>
                )}
                {compact && teamsMarker && <Box component="span" sx={{ ml: 0.75 }}>{teamsMarker}</Box>}
              </Typography>
            </Box>
          )
        )}

        {message.attachments.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
            {message.attachments.map((attachment) => (
              <Paper
                key={attachment.id}
                variant="outlined"
                onClick={() => onOpenAttachment(attachment)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  maxWidth: 320,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {attachmentIcon(attachment)}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {attachment.fileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(attachment.sizeBytes)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}

        {message.reactions.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {message.reactions.map((reaction) => {
              const reacted = reaction.userIds.includes(meId);
              return (
                <Tooltip key={reaction.emoji} title={reactionNames(reaction.userIds)}>
                  <Chip
                    size="small"
                    variant={reacted ? "filled" : "outlined"}
                    color={reacted ? "primary" : "default"}
                    label={`${reaction.emoji} ${reaction.userIds.length}`}
                    onClick={() => onToggleReaction(message, reaction.emoji)}
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        )}

        {onOpenThread && message.replyCount > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            onClick={() => onOpenThread(message.id)}
            sx={{ mt: 0.5, cursor: "pointer", width: "fit-content" }}
          >
            <AvatarGroup
              max={3}
              sx={{ "& .MuiAvatar-root": { width: 20, height: 20, fontSize: 9, borderWidth: 1 } }}
            >
              {message.replyAuthorIds.map((id) => (
                <ChatAvatar key={id} user={usersById.get(id)} size={20} />
              ))}
            </AvatarGroup>
            <Typography variant="caption" color="primary" fontWeight={600}>
              {message.replyCount} {message.replyCount === 1 ? "reply" : "replies"}
            </Typography>
            {message.lastReplyAt && (
              <Typography variant="caption" color="text.secondary">
                Last reply {formatListTime(message.lastReplyAt)}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {showToolbar && (
        <Paper
          elevation={2}
          sx={{
            position: "absolute",
            top: -14,
            right: 20,
            display: "flex",
            alignItems: "center",
            px: 0.25,
            zIndex: 2,
          }}
        >
          {CHAT_QUICK_REACTIONS.slice(0, 4).map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => onToggleReaction(message, emoji)}
              sx={{ fontSize: "1rem", width: 30, height: 30 }}
            >
              {emoji}
            </IconButton>
          ))}
          <Tooltip title="More reactions">
            <IconButton size="small" onClick={(event) => setReactionAnchor(event.currentTarget)}>
              <AddReactionOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          {onOpenThread && (
            <Tooltip title="Reply in thread">
              <IconButton size="small" onClick={() => onOpenThread(message.id)}>
                <ReplyOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {mine && (
            <IconButton size="small" onClick={(event) => setMenuAnchor(event.currentTarget)}>
              <MoreHoriz fontSize="small" />
            </IconButton>
          )}
        </Paper>
      )}

      <Popover
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={() => {
          setReactionAnchor(null);
          setHover(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 36px)", p: 0.5 }}>
          {MORE_REACTIONS.map((emoji) => (
            <IconButton
              key={emoji}
              onClick={() => {
                onToggleReaction(message, emoji);
                setReactionAnchor(null);
                setHover(false);
              }}
              sx={{ fontSize: "1.15rem", width: 36, height: 36 }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>

      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => {
          setMenuAnchor(null);
          setHover(false);
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setEditing(true);
          }}
        >
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setHover(false);
            onDelete(message);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default React.memo(MessageItem);
